import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { UserProfile, Education, generateId } from "@/lib/storage";
import { Plus, Trash2 } from "lucide-react";

interface Props {
  profile: UserProfile;
  onChange: (profile: UserProfile) => void;
}

export default function EducationStep({ profile, onChange }: Props) {
  const addEntry = () => {
    const entry: Education = { id: generateId(), institution: '', degree: '', field: '', graduationYear: '' };
    onChange({ ...profile, education: [...profile.education, entry] });
  };

  const updateEntry = (id: string, field: keyof Education, value: string) => {
    onChange({ ...profile, education: profile.education.map(e => e.id === id ? { ...e, [field]: value } : e) });
  };

  const removeEntry = (id: string) => {
    onChange({ ...profile, education: profile.education.filter(e => e.id !== id) });
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="font-display font-bold text-lg text-foreground">Education</h2>
          <p className="text-sm text-muted-foreground mt-1">Add your educational background.</p>
        </div>
        <Button size="sm" variant="outline" onClick={addEntry} className="gap-1.5">
          <Plus className="w-3.5 h-3.5" /> Add
        </Button>
      </div>

      {profile.education.length === 0 && (
        <div className="text-center py-8 text-muted-foreground text-sm">
          No education added yet.
        </div>
      )}

      {profile.education.map((edu, i) => (
        <div key={edu.id} className="p-4 rounded-xl bg-muted/50 border border-border space-y-3">
          <div className="flex items-center justify-between">
            <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Education {i + 1}</span>
            <button onClick={() => removeEntry(edu.id)} className="text-destructive hover:text-destructive/80 transition-colors">
              <Trash2 className="w-4 h-4" />
            </button>
          </div>
          <div>
            <Label className="text-xs text-muted-foreground">Institution</Label>
            <Input placeholder="MIT" value={edu.institution} onChange={e => updateEntry(edu.id, 'institution', e.target.value)} className="mt-1" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs text-muted-foreground">Degree</Label>
              <Input placeholder="B.S." value={edu.degree} onChange={e => updateEntry(edu.id, 'degree', e.target.value)} className="mt-1" />
            </div>
            <div>
              <Label className="text-xs text-muted-foreground">Field</Label>
              <Input placeholder="Computer Science" value={edu.field} onChange={e => updateEntry(edu.id, 'field', e.target.value)} className="mt-1" />
            </div>
          </div>
          <div>
            <Label className="text-xs text-muted-foreground">Graduation Year</Label>
            <Input placeholder="2023" value={edu.graduationYear} onChange={e => updateEntry(edu.id, 'graduationYear', e.target.value)} className="mt-1" />
          </div>
        </div>
      ))}
    </div>
  );
}
